/**
 * cvrService.js
 * ==============
 * Håndterer CVR-opslag for virksomheder (enkeltmandsvirksomheder og selskaber).
 *
 * VIGTIG LOGIK:
 * Når en bruger indtaster et CVR-nummer, sker følgende:
 *
 * 1. FØRST: Tjek om skyldneren allerede findes i vores egen database
 *    - Kald GET /debtor/search-by-cvr?cvr={cvr}
 *    - Hvis fundet: Brug disse data og marker skyldneren som "eksisterende"
 *
 * 2. HVIS IKKE FUNDET: Slå op i det offentlige CVR-register
 *    - Brug cvrapi.dk (gratis, ingen registrering)
 *    - Auto-udfyld firma-navn og adresse
 *
 * Denne tilgang sikrer at vi:
 * - Genbruger eksisterende skyldner-data
 * - Undgår dubletter i systemet
 * - Kun opretter nye skyldnere når det er nødvendigt
 */

import axios from '@axios'

// ============================================================
// CONFIGURATION
// ============================================================

/**
 * cvrapi.dk er et gratis API til CVR-opslag.
 * Kræver ingen API-nøgle eller registrering.
 * Dokumentation: https://cvrapi.dk/documentation
 */
const CVR_API_BASE_URL = 'https://cvrapi.dk/api'

/**
 * User-Agent header kræves af cvrapi.dk.
 * De vil gerne vide hvem der bruger deres API.
 */
const CVR_API_USER_AGENT = 'DebiaMit/1.0 (kontakt@debtia.dk)'

// ============================================================
// INTERNAL DATABASE LOOKUP
// ============================================================

/**
 * Søger efter en eksisterende skyldner i vores egen database baseret på CVR.
 *
 * BEMÆRK: Dette endpoint skal muligvis oprettes i backend.
 * Hvis det ikke findes, returnerer funktionen null og vi falder
 * tilbage til eksternt CVR-opslag.
 *
 * @param {string} cvr - CVR-nummer (8 cifre)
 * @param {string|number} clientId - Klient-ID for at begrænse søgningen
 * @returns {Object|null} Skyldner-data hvis fundet, ellers null
 */
async function searchInternalDatabase(cvr, clientId) {
  try {
    // Prøv først at søge i den interne database
    // Dette endpoint skal matche jeres backend API
    const response = await axios.get('/debtor/search-by-cvr', {
      params: {
        cvr,
        client_id: clientId,
      },
    })

    if (response.status === 200 && response.data.data) {
      const debtor = response.data.data

      // Returner data i et format der matcher vores debtor-struktur
      return {
        source: 'internal',
        debtorId: debtor.id,
        name: debtor.name || debtor.debtor_name,
        co: debtor.co || '',
        debtorType: debtor.debtor_type || 'capital-company',
        cvr: debtor.cvr_number || debtor.cvr || cvr,
        cpr: debtor.cpr_number || '',
        address: {
          roadName: debtor.road_name || debtor.address_line_1 || '',
          houseNumber: debtor.house_number || '',
          place: debtor.place || '',
          zipCode: debtor.zip_code || debtor.postal_code || '',
          city: debtor.city || '',
          countryId: debtor.country_id || 247,
        },
        phone: debtor.phone || '',
        email: debtor.email || '',
        bankBranchNo: debtor.bank_branch_no || '',
        bankAccountNo: debtor.bank_account_no || '',
        cpn: debtor.cpn || '',
      }
    }

    return null
  } catch (error) {
    // Hvis endpoint ikke findes (404) eller anden fejl, returner null
    // Vi falder så tilbage til eksternt opslag
    console.log('Intern CVR-søgning ikke tilgængelig:', error.message)
    return null
  }
}

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Parser adresse fra cvrapi's response-format.
 * cvrapi returnerer adressen i forskellige felter som vi samler.
 */
function parseExternalAddress(company) {
  // cvrapi kan returnere adressen på forskellige måder
  let roadName = ''
  let houseNumber = ''

  if (company.address) {
    // Prøv at splitte "Gadenavn 123" til separate felter
    const addressParts = company.address.match(/^(.+?)\s+(\d+\S*)$/)
    if (addressParts) {
      [, roadName, houseNumber] = addressParts
    } else {
      roadName = company.address
    }
  }

  return {
    roadName,
    houseNumber,
    place: '', // cvrapi har ikke dette felt
    zipCode: company.zipcode || '',
    city: company.city || '',
    countryId: 247, // Danmark
  }
}

/**
 * Bestemmer debtor-type baseret på virksomhedsform.
 * cvrapi returnerer companydesc som f.eks. "Anpartsselskab", "Enkeltmandsvirksomhed" osv.
 */
function determineDebtorType(companyDesc) {
  if (!companyDesc) return 'capital-company'

  const desc = companyDesc.toLowerCase()

  // Enkeltmandsvirksomhed
  if (desc.includes('enkeltmands') || desc.includes('personlig')) {
    return 'sole-proprietorship'
  }

  // Alt andet behandles som kapitalselskab
  // (A/S, ApS, IVS, K/S, P/S, etc.)
  return 'capital-company'
}

// ============================================================
// EXTERNAL CVR API LOOKUP
// ============================================================

/**
 * Slår et CVR-nummer op i det offentlige CVR-register via cvrapi.dk.
 *
 * @param {string} cvr - CVR-nummer (8 cifre)
 * @returns {Object|null} Virksomhedsdata hvis fundet, ellers null
 *
 * Eksempel på returværdi:
 * {
 *   source: 'cvrapi',
 *   name: 'Firma A/S',
 *   address: {
 *     roadName: 'Gadenavn',
 *     houseNumber: '123',
 *     zipCode: '1234',
 *     city: 'København',
 *   },
 *   phone: '12345678',
 *   email: 'info@firma.dk',
 *   companyType: 'A/S',
 *   startDate: '2020-01-01',
 * }
 */
async function lookupExternalCvr(cvr) {
  try {
    // cvrapi.dk kræver at vi angiver land (dk for Danmark)
    const response = await axios.get(CVR_API_BASE_URL, {
      params: {
        search: cvr,
        country: 'dk',
      },
      headers: {
        'User-Agent': CVR_API_USER_AGENT,
      },
      // Timeout efter 10 sekunder
      timeout: 10000,
    })

    if (response.status === 200 && response.data) {
      const company = response.data

      // Parse adressen - cvrapi returnerer den i forskellige felter
      const address = parseExternalAddress(company)

      // Bestem debtor-type baseret på virksomhedsform
      const debtorType = determineDebtorType(company.companydesc)

      return {
        source: 'cvrapi',
        name: company.name || '',
        cvr: company.vat || cvr,
        debtorType,
        address,
        phone: company.phone || '',
        email: company.email || '',
        // Ekstra info der kan være nyttigt
        companyType: company.companydesc || '',
        startDate: company.startdate || '',
        industry: company.industrydesc || '',
      }
    }

    return null
  } catch (error) {
    // Håndter specifikke fejl fra cvrapi
    if (error.response) {
      if (error.response.status === 404) {
        // CVR-nummer ikke fundet
        return null
      }
      if (error.response.status === 429) {
        // Rate limit nået
        throw new Error('For mange forespørgsler. Vent venligst et øjeblik.')
      }
    }

    console.error('Eksternt CVR-opslag fejlede:', error.message)
    throw new Error('Kunne ikke slå CVR op. Prøv igen senere.')
  }
}

// ============================================================
// MAIN LOOKUP FUNCTION
// ============================================================

/**
 * Hovedfunktion til CVR-opslag med den ønskede logik:
 * 1. Først søg i intern database
 * 2. Hvis ikke fundet, søg i eksternt CVR-register
 *
 * @param {string} cvr - CVR-nummer (8 cifre)
 * @param {string|number} clientId - Klient-ID for intern søgning
 * @returns {Object} Resultat med data og kilde
 *
 * Returværdi:
 * {
 *   found: true/false,
 *   source: 'internal' | 'cvrapi' | null,
 *   isExistingDebtor: true/false,  // Om skyldneren findes i vores system
 *   data: { ... },
 *   message: 'Besked til brugeren',
 * }
 */
async function lookupCvr(cvr, clientId) {
  // Valider CVR-format
  const cleanCvr = cvr.replace(/\s/g, '')
  if (!/^\d{8}$/.test(cleanCvr)) {
    return {
      found: false,
      source: null,
      isExistingDebtor: false,
      data: null,
      message: 'CVR-nummer skal være 8 cifre',
    }
  }

  // Step 1: Søg i intern database
  const internalResult = await searchInternalDatabase(cleanCvr, clientId)

  if (internalResult) {
    return {
      found: true,
      source: 'internal',
      isExistingDebtor: true,
      data: internalResult,
      message: 'Skyldner findes allerede i systemet',
    }
  }

  // Step 2: Søg i eksternt CVR-register
  try {
    const externalResult = await lookupExternalCvr(cleanCvr)

    if (externalResult) {
      return {
        found: true,
        source: 'cvrapi',
        isExistingDebtor: false,
        data: externalResult,
        message: 'Virksomhed fundet i CVR-registret',
      }
    }

    return {
      found: false,
      source: null,
      isExistingDebtor: false,
      data: null,
      message: 'CVR-nummer ikke fundet',
    }
  } catch (error) {
    return {
      found: false,
      source: null,
      isExistingDebtor: false,
      data: null,
      message: error.message,
    }
  }
}

// ============================================================
// VALIDATION HELPERS
// ============================================================

/**
 * Validerer et CVR-nummer.
 * Danske CVR-numre er 8 cifre.
 */
function isValidCvr(cvr) {
  if (!cvr) return false
  const cleanCvr = cvr.replace(/\s/g, '')
  return /^\d{8}$/.test(cleanCvr)
}

/**
 * Formaterer et CVR-nummer til visning.
 * "12345678" -> "12 34 56 78"
 */
function formatCvr(cvr) {
  if (!cvr) return ''
  const cleanCvr = cvr.replace(/\s/g, '')
  if (cleanCvr.length !== 8) return cvr
  return cleanCvr.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4')
}

// ============================================================
// EXPORTS
// ============================================================

export {
  lookupCvr,
  searchInternalDatabase,
  lookupExternalCvr,
  isValidCvr,
  formatCvr,
}

export default {
  lookupCvr,
  isValidCvr,
  formatCvr,
}
